import { useCallback, useEffect, useMemo, useRef } from 'react';
import { APOLLO_FETCH_POLICY } from '@/constants/apollo';
import { setDefaultHeader } from '@/publicSearch/graphql/headers';
import { useGetMembersLazyQuery } from '@/publicSearch/graphql/queries.generated';
import useFilterStore from '@/publicSearch/store/useFilterStore';
import { SEARCH_TYPE } from '@/constants/searchType';
import useGridStore from '@/publicSearch/store/useGridStore';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import {
  FieldOption,
  type GetMembersRow,
} from '@/components/publicSearch/constants';
import { SortOrderEnum } from '@/lib/types';
import { useStartSearchOnMount } from './useStartSearchOnMount';
import type {
  MemberSortField,
  SortDirectionOrder,
} from '@/graphqlClient/types';
import {
  buildMemberFilterGroups,
  buildMemberFilterGroupsFromGroups,
} from './buildMemberFilterGroups';
import {
  parseMemberListFieldsConfig,
  buildMemberColumns,
  buildMemberRows,
} from '@/hooks/publicSearch/member/memberGrid';

const SortDirectionMap: Record<string, SortDirectionOrder> = {
  [SortOrderEnum.ASC]: 'ASCENDING',
  [SortOrderEnum.DESC]: 'DESCENDING',
};

// Grid column keys don't always match the MemberSortField enum on the server.
// Map row-key aliases → valid MemberSortField values; strip the underscore
// from SOCIETY_MEMBER_FIELD_N row keys to match the schema (SOCIETY_MEMBER_FIELDN).
const MemberSortFieldAliases: Record<string, MemberSortField> = {
  MEMBER: 'MEMBER_NAME',
  MEMB_NAME: 'MEMBER_NAME',
  MEMB_STATUS: 'MEMBER_STATUS',
  MEMB_AUTH: 'MEMBER_AUTH',
};

const VALID_MEMBER_SORT_FIELDS = new Set<MemberSortField>([
  'ADDRESS1',
  'ADDRESS2',
  'ADDRESS3',
  'BREEDPLAN_FLAG',
  'DETAILS_AVAIL_FLAG',
  'EMAIL',
  'HERD_SIZE',
  'HERD_SIZE_DESC',
  'HOMEPAGE',
  'ITEM_KEY',
  'MAP_DETAILS',
  'MEMBER_AUTH',
  'MEMBER_ID',
  'MEMBER_IDENT',
  'MEMBER_NAME',
  'MEMBER_NOTES',
  'MEMBER_STATUS',
  'MEMBER_TYPE',
  'PHONE_DETAILS',
  'POSTCODE',
  'PREFIX',
  'RENEWAL_DATE',
  'RENEWAL_FEE',
  'SOCIETY_CODE',
  'SOCIETY_ID',
  'SOCIETY_MEMBER_FIELD1',
  'SOCIETY_MEMBER_FIELD2',
  'SOCIETY_MEMBER_FIELD3',
  'SOCIETY_MEMBER_FIELD4',
  'SOCIETY_MEMBER_FIELD5',
  'SOCIETY_MEMBER_FIELD6',
  'SOCIETY_MEMBER_FIELD7',
  'SOCIETY_MEMBER_FIELD8',
  'SOCIETY_MEMBER_FIELD9',
  'SOCIETY_MEMBER_FIELD10',
  'SOCIETY_MEMBER_FIELD11',
  'SOCIETY_MEMBER_FIELD12',
  'SOCIETY_MEMBER_FIELD13',
  'SOCIETY_MEMBER_FIELD14',
  'SOCIETY_MEMBER_FIELD15',
  'SORTNAME',
]);

function toMemberSortField(field: string): MemberSortField | null {
  if (!field) return null;
  if (MemberSortFieldAliases[field]) return MemberSortFieldAliases[field];
  const socMatch = field.match(/^SOCIETY_MEMBER_FIELD_(\d+)$/);
  if (socMatch) {
    const candidate = `SOCIETY_MEMBER_FIELD${socMatch[1]}` as MemberSortField;
    return VALID_MEMBER_SORT_FIELDS.has(candidate) ? candidate : null;
  }
  return VALID_MEMBER_SORT_FIELDS.has(field as MemberSortField)
    ? (field as MemberSortField)
    : null;
}

export function useMembersFilter(enabled: boolean) {
  const orgId = usePublicSearchContext((context) => context.society.orgId);
  const orgCode = usePublicSearchContext(
    (context) => context.society.societyCode!,
  );
  const society = usePublicSearchContext((context) => context.society);
  const {
    filters,
    commonFilterOperator,
    filterGroups: storeFilterGroups,
    groupsOperator,
  } = useFilterStore(SEARCH_TYPE.MEMBERS);
  const { gridSortModel: sortModel, paginationModel } = useGridStore();

  // The AdvancedFilterDialog drives `storeFilterGroups`; when it has any
  // pills the grouped builder owns the GraphQL payload so multi-group AND/OR
  // logic survives. The flat `filters` path keeps powering the basic search
  // (hero bar + Basic Search dialog) untouched. Mirrors useAnimalEbvFilter.
  const useGrouped = storeFilterGroups.length > 0;

  const memberListFieldsConfig = useMemo(
    () => parseMemberListFieldsConfig(society.settings),
    [society.settings],
  );

  const [getMembers, { data, error, loading, previousData, refetch }] =
    useGetMembersLazyQuery({
      fetchPolicy: APOLLO_FETCH_POLICY.NETWORK_ONLY,
    });
  // See useAnimalEbvFilter for the rationale: route the *next* startSearch
  // through refetch() only when we have a tracked error, so the grid recovers
  // from a stuck ObservableQuery after errors / aborts without needing a
  // society-switch + apolloClient.resetStore(). Happy-path startSearches
  // continue to use the lazy execute.
  const recoveredFromErrorRef = useRef<unknown>(null);

  // See useSaleCatalogueFilter — Apollo's lazy-query handles can swap
  // reference between the render that fires the first execute and the
  // render that commits the response. Read them through refs so the
  // memoized startSearch only changes when user-meaningful state changes
  // (filters / sort / pagination / orgId / enabled), and not on the
  // post-fetch render that would otherwise re-trigger
  // useStartSearchOnMount and double-fire the request.
  const getMembersRef = useRef<typeof getMembers>(getMembers);
  const refetchRef = useRef<typeof refetch>(refetch);
  const errorRef = useRef<typeof error>(error);
  // Keep latest-value refs in sync after each commit (read by the stable callback below).
  useEffect(() => {
    getMembersRef.current = getMembers;
    refetchRef.current = refetch;
    errorRef.current = error;
  });

  const startSearch = useCallback(() => {
    if (!enabled || !orgId) return;

    const variables = {
      requestInput: {
        pagination: {
          currentPage: (paginationModel.page ?? 0) + 1,
          pageSize: paginationModel.pageSize,
        },
        sortOptions: sortModel
          .map(({ field, sort }) => ({
            direction: SortDirectionMap[sort!] as SortDirectionOrder,
            field: toMemberSortField(field) as MemberSortField,
          }))
          .filter((it) => it.direction && it.field),
        filterGroups: useGrouped
          ? buildMemberFilterGroupsFromGroups(
              storeFilterGroups,
              groupsOperator,
              society.settings,
            )
          : buildMemberFilterGroups(
              filters,
              commonFilterOperator,
              society.settings,
            ),
      },
    };
    const context = { headers: setDefaultHeader(orgId) };

    const currentError = errorRef.current;
    if (currentError && recoveredFromErrorRef.current !== currentError) {
      recoveredFromErrorRef.current = currentError;
      try {
        void refetchRef.current(variables);
      } catch {
        getMembersRef.current({ variables, context });
      }
      return;
    }
    getMembersRef.current({ variables, context });
  }, [
    commonFilterOperator,
    enabled,
    filters,
    groupsOperator,
    orgId,
    // Depend on the *primitive* page/pageSize, not the whole object —
    // PublicSearchInput.tsx fires setPaginationModel({page:0, pageSize:…})
    // on mount, which creates a brand-new object reference even when the
    // values are unchanged. Listing `paginationModel` here would invalidate
    // startSearch on every mount and cause useStartSearchOnMount to fire a
    // duplicate POST. useAnimalEbvFilter already follows this pattern.
    paginationModel.page,
    paginationModel.pageSize,
    society.settings,
    sortModel,
    storeFilterGroups,
    useGrouped,
  ]);

  // Content signature of the request inputs — de-dupes redundant fires
  // (StrictMode + stale-then-fresh society.settings churn) in
  // useStartSearchOnMount while still re-firing on real filter/sort/page
  // changes. This hook builds filterGroups inline inside startSearch, so we
  // sign the raw inputs that feed it. See useStartSearchOnMount for the
  // full rationale.
  const searchSignature = useMemo(
    () =>
      JSON.stringify({
        useGrouped,
        storeFilterGroups,
        groupsOperator,
        filters,
        commonFilterOperator,
        settings: society.settings,
        sortModel,
        orgId,
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
      }),
    [
      useGrouped,
      storeFilterGroups,
      groupsOperator,
      filters,
      commonFilterOperator,
      society.settings,
      sortModel,
      orgId,
      paginationModel.page,
      paginationModel.pageSize,
    ],
  );

  useStartSearchOnMount(startSearch, enabled, searchSignature);

  // Render rows from `data ?? previousData` instead of clearing them
  // whenever `loading` is true. MUI's `loading` overlay already gives the
  // user a clear "fetching" signal — emptying the grid underneath it on
  // every refetch is what produced the visible "data being rerendered"
  // jank: every pagination change / filter / sort flipped loading=true,
  // this useMemo returned [] for one render, the grid blanked, then
  // loading=false flipped back and the grid re-populated from scratch.
  // Keeping `previousData` rows during the loading window means the grid
  // stays visually stable and only the row content swaps when the new
  // payload arrives.
  const rows = useMemo(
    () => buildMemberRows((data ?? previousData)?.members?.items),
    [data, previousData],
  );

  const getRowId = (row: GetMembersRow) => {
    return row[FieldOption.memberId.value];
  };

  const columns = useMemo(
    () => buildMemberColumns(memberListFieldsConfig, orgCode),
    [memberListFieldsConfig, orgCode],
  );

  return useMemo(
    () => ({
      data: data?.members,
      previousData: previousData?.members,
      error,
      loading,
      done: (data || error) && !loading,
      dataGrid: { columns, rows, getRowId },
      startSearch,
    }),
    [data, error, loading, previousData, rows, startSearch, columns],
  );
}
